import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import {
  Home, DollarSign, BarChart3, Sparkles, Landmark, Inbox, MessageSquare,
  TrendingUp, LogOut, ExternalLink, ChevronRight, Edit2, ArrowUpRight,
  Eye, MailOpen, Trash2, Download, Globe, Mail, Bot, ClipboardList,
} from "lucide-react";
import {
  LineChart, Line, ResponsiveContainer,
} from "recharts";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { FUNDING_TOOLS } from "@/lib/funding_tools";
import AnalyticsPanel from "@/components/admin/AnalyticsPanel";
import IncomePanel from "@/components/admin/IncomePanel";

const NAV = [
  { id: "home", label: "Home", icon: Home },
  { id: "income", label: "Getting Paid", icon: DollarSign },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
  { id: "leads", label: "AI Readiness", icon: Sparkles },
  { id: "funding", label: "Funding Readiness", icon: Landmark },
  { id: "inquiries", label: "Discovery Inquiries", icon: Inbox },
  { id: "contacts", label: "Contact Messages", icon: MessageSquare },
  { id: "chats", label: "Conversations", icon: Bot },
  { id: "realitycheck", label: "Reality Check", icon: ClipboardList },
  { id: "affiliate", label: "Affiliate Clicks", icon: TrendingUp },
];

const fmt = (cents) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format((cents || 0) / 100);

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const [section, setSection] = useState(() => localStorage.getItem("tmg_admin_section") || "home");
  const [overview, setOverview] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [leads, setLeads] = useState([]);
  const [fundingLeads, setFundingLeads] = useState([]);
  const [affiliate, setAffiliate] = useState({ tools: [], total_clicks: 0 });
  const [chatSessions, setChatSessions] = useState([]);
  const [rcLeads, setRcLeads] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => { localStorage.setItem("tmg_admin_section", section); }, [section]);

  const loadAll = async () => {
    const [ov, c, i, l, fl, af, cs, rc] = await Promise.all([
      api.get("/admin/overview"),
      api.get("/admin/contacts"),
      api.get("/admin/inquiries"),
      api.get("/admin/leads"),
      api.get("/admin/funding-leads"),
      api.get("/admin/affiliate-clicks"),
      api.get("/admin/chat-sessions"),
      api.get("/admin/reality-check-leads"),
    ]);
    setOverview(ov.data);
    setContacts(c.data);
    setInquiries(i.data);
    setLeads(l.data);
    setFundingLeads(fl.data);
    setAffiliate(af.data);
    setChatSessions(cs.data);
    setRcLeads(rc.data);
  };

  useEffect(() => {
    if (user) loadAll().catch(() => toast.error("Failed to load admin data"));
  }, [user]);

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (user.role !== "admin") return <Navigate to="/my-account" replace />;

  const markRead = async (type, id) => {
    await api.patch(`/admin/${type}/${id}/read`);
    await loadAll();
  };
  const remove = async (type, id) => {
    if (!confirm("Delete this submission?")) return;
    await api.delete(`/admin/${type}/${id}`);
    setSelected(null);
    await loadAll();
    toast.success("Deleted");
  };

  const totals = overview?.totals || {};

  return (
    <div className="min-h-screen bg-[var(--tmg-ivory-2)]/50 flex" data-testid="admin-dashboard">
      {/* SIDEBAR */}
      <aside className="hidden lg:flex w-64 bg-[var(--tmg-obsidian)] text-white flex-col sticky top-0 h-screen">
        <div className="p-6 border-b border-white/10">
          <Link to="/" className="flex items-center gap-3">
            <span className="w-9 h-9 bg-[var(--tmg-gold)] text-[var(--tmg-obsidian)] flex items-center justify-center font-display font-bold text-[10px] tracking-tight">TMG</span>
            <div>
              <p className="font-display text-base font-semibold leading-tight">TMG Consulting Group</p>
              <p className="text-[10px] text-white/50 font-mono-sm uppercase tracking-wider">Admin Console</p>
            </div>
          </Link>
        </div>

        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
          {NAV.map((n) => {
            const count = countFor(n.id, totals, leads.length, fundingLeads.length, inquiries.length, contacts.length, affiliate.total_clicks);
            const unread = unreadFor(n.id, totals);
            const active = section === n.id;
            return (
              <button
                key={n.id}
                onClick={() => setSection(n.id)}
                data-testid={`sidebar-${n.id}`}
                className={`w-full flex items-center justify-between px-3 py-2.5 text-sm transition-all ${
                  active ? "bg-[var(--tmg-gold)] text-[var(--tmg-obsidian)] font-semibold" : "text-white/75 hover:bg-white/5 hover:text-white"
                }`}
              >
                <span className="flex items-center gap-3">
                  <n.icon className="w-4 h-4" />
                  {n.label}
                </span>
                <span className="flex items-center gap-1.5">
                  {unread > 0 && !active && (
                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--tmg-gold)]" />
                  )}
                  {count !== null && (
                    <span className={`text-[10px] font-mono-sm ${active ? "text-[var(--tmg-obsidian)]/70" : "text-white/40"}`}>
                      {count}
                    </span>
                  )}
                  <ChevronRight className={`w-3 h-3 ${active ? "opacity-100" : "opacity-30"}`} />
                </span>
              </button>
            );
          })}
        </nav>

        <div className="p-4 border-t border-white/10">
          <Link to="/" className="flex items-center gap-2 text-xs text-white/60 hover:text-[var(--tmg-gold)] mb-3">
            <ExternalLink className="w-3 h-3" /> View public site
          </Link>
          <button onClick={logout} data-testid="admin-logout-btn" className="flex items-center gap-2 text-xs text-white/60 hover:text-[var(--tmg-gold)]">
            <LogOut className="w-3 h-3" /> Sign out
          </button>
        </div>
      </aside>

      {/* MOBILE NAV */}
      <div className="lg:hidden w-full bg-[var(--tmg-obsidian)] text-white p-3 flex items-center gap-2 overflow-x-auto sticky top-0 z-20">
        {NAV.map((n) => (
          <button
            key={n.id}
            onClick={() => setSection(n.id)}
            data-testid={`mobile-sidebar-${n.id}`}
            className={`flex items-center gap-2 px-3 py-2 text-xs whitespace-nowrap ${section === n.id ? "bg-[var(--tmg-gold)] text-[var(--tmg-obsidian)] font-semibold" : "text-white/70"}`}
          >
            <n.icon className="w-3.5 h-3.5" />
            {n.label}
          </button>
        ))}
        <button onClick={logout} className="ml-auto px-3 py-2 text-xs text-white/60"><LogOut className="w-3.5 h-3.5" /></button>
      </div>

      {/* MAIN */}
      <main className="flex-1 min-w-0">
        {section === "home" && overview && (
          <HomeView
            overview={overview}
            onJump={setSection}
          />
        )}
        {section === "income" && <IncomePanel />}
        {section === "analytics" && <AnalyticsPanel />}
        {section === "leads" && (
          <SectionWrap title="AI Readiness Leads" subtitle={`${leads.length} total`}>
            <LeadsList items={leads} endpoint="leads" onView={(item) => setSelected({ type: "leads", item })} onRead={(id) => markRead("leads", id)} onDelete={(id) => remove("leads", id)} />
          </SectionWrap>
        )}
        {section === "funding" && (
          <SectionWrap title="Funding Readiness Leads" subtitle={`${fundingLeads.length} total`}>
            <LeadsList items={fundingLeads} endpoint="funding-leads" onView={(item) => setSelected({ type: "funding-leads", item })} onRead={(id) => markRead("funding-leads", id)} onDelete={(id) => remove("funding-leads", id)} />
          </SectionWrap>
        )}
        {section === "inquiries" && (
          <SectionWrap title="Discovery Inquiries" subtitle={`${inquiries.length} total`}>
            <SubmissionsList items={inquiries} type="inquiries" onView={(item) => setSelected({ type: "inquiries", item })} onRead={(id) => markRead("inquiries", id)} onDelete={(id) => remove("inquiries", id)} />
          </SectionWrap>
        )}
        {section === "contacts" && (
          <SectionWrap title="Contact Messages" subtitle={`${contacts.length} total`}>
            <SubmissionsList items={contacts} type="contacts" onView={(item) => setSelected({ type: "contacts", item })} onRead={(id) => markRead("contacts", id)} onDelete={(id) => remove("contacts", id)} />
          </SectionWrap>
        )}
        {section === "affiliate" && <AffiliateAnalytics data={affiliate} onReload={loadAll} />}
        {section === "chats" && (
          <SectionWrap title="Conversations" subtitle={`${chatSessions.length} chat sessions`}>
            <ConversationsPanel sessions={chatSessions} />
          </SectionWrap>
        )}
        {section === "realitycheck" && (
          <SectionWrap title="CEO Reality Check" subtitle={`${rcLeads.length} completions`}>
            <RealityCheckPanel leads={rcLeads} onReload={loadAll} />
          </SectionWrap>
        )}
      </main>

      {selected && (
        <DetailModal selection={selected} onClose={() => setSelected(null)} onDelete={(id) => remove(selected.type, id)} />
      )}
    </div>
  );
}

function countFor(id, totals, leadsLen, fundingLen, inqLen, contactsLen, affTotal) {
  switch (id) {
    case "leads": return leadsLen;
    case "funding": return fundingLen;
    case "inquiries": return inqLen;
    case "contacts": return contactsLen;
    case "affiliate": return affTotal;
    default: return null;
  }
}
function unreadFor(id, totals) {
  switch (id) {
    case "leads": return totals.leads_new || 0;
    case "funding": return totals.funding_leads_new || 0;
    case "inquiries": return totals.inquiries_new || 0;
    case "contacts": return totals.contacts_new || 0;
    default: return 0;
  }
}

function SectionWrap({ title, subtitle, children }) {
  return (
    <div>
      <header className="bg-white border-b border-[var(--tmg-line)] px-8 py-6">
        <p className="tmg-overline mb-1">— Admin</p>
        <h1 className="font-display text-2xl font-semibold">{title}</h1>
        {subtitle && <p className="text-xs text-[var(--tmg-muted)] font-mono-sm mt-1">{subtitle}</p>}
      </header>
      <div className="bg-white border border-[var(--tmg-line)] m-8">{children}</div>
    </div>
  );
}

/* ===========================================================
   REALITY CHECK completions report
   =========================================================== */
function RealityCheckPanel({ leads, onReload }) {
  const [busyId, setBusyId] = useState(null);

  const stopEmails = async (lead) => {
    setBusyId(lead.id);
    try {
      const res = await api.post("/admin/sequences/stop", { email: lead.email });
      toast.success(`Stopped ${res.data.stopped} scheduled email(s) for ${lead.email}`);
      onReload();
    } catch {
      toast.error("Failed to stop sequence");
    }
    setBusyId(null);
  };

  if (!leads.length) {
    return <div className="p-8 text-sm text-[var(--tmg-muted)]">No Reality Check completions yet.</div>;
  }

  const AUDIENCE_LABELS = { business: "Business owner / team", solo: "Solopreneur", nonprofit: "Nonprofit" };
  const tallyBy = (key, labelMap) => {
    const m = {};
    leads.forEach((l) => { const k = l[key] || ""; m[k] = (m[k] || 0) + 1; });
    return Object.entries(m)
      .sort((a, b) => b[1] - a[1])
      .map(([k, n]) => ({ label: (labelMap && labelMap[k]) || k || "No tag", n }));
  };
  const groups = [
    { title: "By audience", rows: tallyBy("audience", AUDIENCE_LABELS), tid: "rc-breakdown-audience" },
    { title: "By A/B variant", rows: tallyBy("variant"), tid: "rc-breakdown-variant" },
    { title: "By source", rows: tallyBy("source"), tid: "rc-breakdown-source" },
  ];

  return (
    <div>
      <div data-testid="rc-breakdowns" className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-5 border-b border-[var(--tmg-line)]">
        {groups.map((g) => (
          <div key={g.tid} data-testid={g.tid} className="rounded-lg border border-[var(--tmg-line)] p-4">
            <div className="text-[10px] font-mono-sm uppercase tracking-[0.15em] text-[var(--tmg-muted)] mb-2">{g.title}</div>
            {g.rows.map((r) => (
              <div key={r.label} className="flex justify-between text-sm py-1">
                <span>{r.label}</span>
                <span className="font-semibold" data-testid={`${g.tid}-count-${r.label}`}>{r.n}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] font-mono-sm uppercase tracking-[0.15em] text-[var(--tmg-muted)] border-b border-[var(--tmg-line)]">
            <th className="px-5 py-3">Name</th>
            <th className="px-5 py-3">Email</th>
            <th className="px-5 py-3">Score</th>
            <th className="px-5 py-3">Source</th>
            <th className="px-5 py-3">Variant</th>
            <th className="px-5 py-3">Sequence</th>
            <th className="px-5 py-3">Date</th>
            <th className="px-5 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {leads.map((l, i) => (
            <tr key={l.id} data-testid={`rc-lead-row-${i}`} className="border-b border-[var(--tmg-line)]/60 last:border-0 hover:bg-[var(--tmg-ivory)]/60 transition-colors">
              <td className="px-5 py-3 font-medium">{l.first_name}</td>
              <td className="px-5 py-3">{l.email}</td>
              <td className="px-5 py-3 whitespace-nowrap">
                <span className="font-semibold">{l.total_pct}%</span>{" "}
                <span className="text-[var(--tmg-muted)]">{l.result}</span>
              </td>
              <td className="px-5 py-3">{l.source || "—"}</td>
              <td className="px-5 py-3">{l.variant || "—"}</td>
              <td className="px-5 py-3">
                {l.pending_count > 0 ? (
                  <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">{l.pending_count} scheduled</Badge>
                ) : (
                  <Badge className="bg-[var(--tmg-ivory-2)] text-[var(--tmg-muted)] border-[var(--tmg-line)]">done / stopped</Badge>
                )}
              </td>
              <td className="px-5 py-3 text-[var(--tmg-muted)] whitespace-nowrap">
                {new Date(l.created_at).toLocaleDateString()}
              </td>
              <td className="px-5 py-3">
                {l.pending_count > 0 && (
                  <button
                    data-testid={`rc-stop-btn-${i}`}
                    onClick={() => stopEmails(l)}
                    disabled={busyId === l.id}
                    className="text-xs font-semibold text-red-600 hover:text-red-800 disabled:opacity-40"
                  >
                    Stop emails
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

/* ===========================================================
   CONVERSATIONS (chat assistant transcripts)
   =========================================================== */
function ConversationsPanel({ sessions }) {
  const [openId, setOpenId] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [loadingId, setLoadingId] = useState(null);

  const toggle = async (sid) => {
    if (openId === sid) {
      setOpenId(null);
      setTranscript(null);
      return;
    }
    setOpenId(sid);
    setTranscript(null);
    setLoadingId(sid);
    try {
      const res = await api.get(`/admin/chat-sessions/${sid}`);
      setTranscript(res.data);
    } catch {
      toast.error("Failed to load transcript");
    }
    setLoadingId(null);
  };

  if (!sessions.length) {
    return <div className="p-8 text-sm text-[var(--tmg-muted)]">No conversations yet.</div>;
  }

  return (
    <div>
      {sessions.map((s, i) => (
        <div key={s.session_id} className="border-b border-[var(--tmg-line)] last:border-0">
          <button
            data-testid={`chat-session-row-${i}`}
            onClick={() => toggle(s.session_id)}
            className="w-full text-left px-6 py-4 hover:bg-[var(--tmg-ivory)] transition-colors"
          >
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{s.preview || "(no visitor messages)"}</p>
                <p className="text-xs text-[var(--tmg-muted)] font-mono-sm mt-1">
                  {s.message_count} messages · {new Date(s.last_at).toLocaleString()}
                </p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {s.lead_email && (
                  <Badge
                    data-testid={`chat-lead-badge-${i}`}
                    className="bg-[var(--tmg-gold)]/15 text-[var(--tmg-gold-soft)] border-[var(--tmg-gold)]/30"
                  >
                    {s.lead_name ? `${s.lead_name} · ` : ""}{s.lead_email}
                  </Badge>
                )}
                <ChevronRight className={`w-4 h-4 transition-transform ${openId === s.session_id ? "rotate-90" : ""}`} />
              </div>
            </div>
          </button>
          {openId === s.session_id && (
            <div data-testid={`chat-transcript-${i}`} className="px-6 pb-5 bg-[var(--tmg-ivory)]/60">
              {loadingId === s.session_id && (
                <p className="text-xs text-[var(--tmg-muted)] py-3">Loading…</p>
              )}
              {transcript && (
                <div className="space-y-2 pt-2 max-h-96 overflow-y-auto">
                  {transcript.messages.map((m, j) => (
                    <div
                      key={j}
                      className={`max-w-[85%] px-3 py-2 text-xs leading-relaxed whitespace-pre-wrap ${
                        m.role === "user"
                          ? "ml-auto bg-[var(--tmg-obsidian)] text-[var(--tmg-ivory)]"
                          : "mr-auto bg-white border border-[var(--tmg-line)]"
                      }`}
                    >
                      {m.text}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/* ===========================================================
   HOME VIEW (Wix-inspired)
   =========================================================== */
function HomeView({ overview, onJump }) {
  const { sessions, revenue, totals, activity, business } = overview;
  const sparkData = sessions.sparkline.map((d) => ({ day: d.day, v: d.sessions }));
  const delta = sessions.delta_pct;

  return (
    <div data-testid="admin-home" className="p-6 sm:p-10 space-y-8">
      {/* Welcome header */}
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold mb-1">Welcome back, Twana</h1>
        <p className="text-sm text-[var(--tmg-muted)]">Here's what's happening at TMG Consulting Group right now.</p>
      </div>

      {/* Business info row */}
      <div className="bg-white border border-[var(--tmg-line)] p-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <InfoCell label="Business" value={business.name} />
        <InfoCell label="Domain" value={business.domain} icon={Globe} link={`https://${business.domain}`} />
        <InfoCell label="Business email" value={business.email} icon={Mail} link={`mailto:${business.email}`} />
        <InfoCell label="Public site" value="View live" link="/" icon={ExternalLink} testId="home-view-public" />
      </div>

      {/* Key stats */}
      <div>
        <div className="flex items-end justify-between mb-4">
          <div>
            <p className="tmg-overline mb-1">— Analytics</p>
            <h2 className="font-display text-xl font-semibold">Your key stats for the last 30 days</h2>
          </div>
          <button onClick={() => onJump("analytics")} data-testid="home-view-analytics" className="text-xs font-semibold text-[var(--tmg-obsidian)] hover:text-[var(--tmg-gold-soft)] border-b border-[var(--tmg-obsidian)] hover:border-[var(--tmg-gold)] pb-0.5 uppercase tracking-[0.14em]">
            View Site Analytics →
          </button>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            testId="home-stat-sessions"
            label="Site sessions"
            value={sessions.last_30_days.toLocaleString()}
            sub={`${sessions.today} today · ${sessions.yesterday} yesterday`}
            delta={delta}
            spark={sparkData}
          />
          <StatCard
            testId="home-stat-revenue"
            label="Total revenue (30d)"
            value={fmt(revenue.last_30_days_cents)}
            sub={`${revenue.orders_30} ${revenue.orders_30 === 1 ? "entry" : "entries"}`}
          />
          <StatCard
            testId="home-stat-leads"
            label="New leads"
            value={(totals.leads_new + totals.funding_leads_new).toString()}
            sub={`${totals.leads + totals.funding_leads} all-time`}
          />
          <StatCard
            testId="home-stat-inquiries"
            label="Discovery inquiries"
            value={totals.inquiries_new.toString()}
            sub={`${totals.inquiries} all-time`}
          />
        </div>
      </div>

      {/* Quick actions + Activity feed */}
      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 bg-white border border-[var(--tmg-line)]">
          <div className="px-6 py-5 border-b border-[var(--tmg-line)] flex items-center justify-between">
            <div>
              <p className="tmg-overline mb-1">— Activity</p>
              <h3 className="font-display text-lg font-semibold">Recent activity feed</h3>
            </div>
            <span className="text-xs text-[var(--tmg-muted)] font-mono-sm">Latest 15</span>
          </div>
          {activity.length === 0 ? (
            <div className="p-12 text-center text-[var(--tmg-muted)] text-sm">
              No activity yet. Once visitors fill out forms or you log income, you'll see it here.
            </div>
          ) : (
            <div className="divide-y divide-[var(--tmg-line)]">
              {activity.map((a, i) => (
                <ActivityRow key={`${a.kind}-${a.id || i}`} item={a} onJump={onJump} />
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-4 space-y-4">
          <div className="bg-[var(--tmg-obsidian)] text-white p-6">
            <p className="tmg-overline-gold mb-2">— Quick Actions</p>
            <h3 className="font-display text-lg font-semibold mb-5">Jump back in</h3>
            <div className="space-y-2">
              <QuickAction onClick={() => onJump("income")} icon={DollarSign} label="Log income" testId="qa-income" />
              <QuickAction onClick={() => onJump("analytics")} icon={BarChart3} label="Site analytics" testId="qa-analytics" />
              <QuickAction onClick={() => onJump("leads")} icon={Sparkles} label="Review AI leads" testId="qa-leads" badge={totals.leads_new} />
              <QuickAction onClick={() => onJump("funding")} icon={Landmark} label="Funding leads" testId="qa-funding" badge={totals.funding_leads_new} />
              <QuickAction onClick={() => onJump("inquiries")} icon={Inbox} label="Discovery inquiries" testId="qa-inquiries" badge={totals.inquiries_new} />
            </div>
          </div>

          <div className="bg-white border border-[var(--tmg-line)] p-6">
            <p className="tmg-overline mb-2">— Suggested for you</p>
            <h3 className="font-display text-base font-semibold mb-3">Wire up your domain &amp; email</h3>
            <p className="text-xs text-[var(--tmg-muted)] mb-4 leading-relaxed">
              When you're ready to send nurture emails &amp; capture leads to your real inbox, add your Brevo
              API key + verified sender — and switch your GoDaddy nameservers so SSL provisions on
              www.tmariegreene.com.
            </p>
            <Link to="/" className="text-xs font-semibold text-[var(--tmg-obsidian)] border-b border-[var(--tmg-obsidian)] hover:text-[var(--tmg-gold-soft)] hover:border-[var(--tmg-gold)] pb-0.5 uppercase tracking-[0.14em]">
              View public site →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoCell({ label, value, icon: Icon, link, action, testId }) {
  const Wrapper = link ? (link.startsWith("http") || link.startsWith("mailto") ? "a" : Link) : "div";
  const props = link
    ? (link.startsWith("http") ? { href: link, target: "_blank", rel: "noopener noreferrer" } : link.startsWith("mailto") ? { href: link } : { to: link })
    : {};
  return (
    <div data-testid={testId}>
      <p className="tmg-overline mb-2">{label}</p>
      <Wrapper {...props} className={`flex items-center gap-2 ${link ? "hover:text-[var(--tmg-gold-soft)]" : ""}`}>
        {Icon && <Icon className="w-3.5 h-3.5 text-[var(--tmg-gold)]" />}
        <span className="text-sm font-medium truncate">{value}</span>
      </Wrapper>
      {action && (
        <button className="mt-1 text-xs text-[var(--tmg-muted)] hover:text-[var(--tmg-obsidian)] flex items-center gap-1">
          <Edit2 className="w-3 h-3" /> {action}
        </button>
      )}
    </div>
  );
}

function StatCard({ label, value, sub, delta, spark, testId }) {
  return (
    <div data-testid={testId} className="bg-white border border-[var(--tmg-line)] p-5 relative overflow-hidden">
      <p className="tmg-overline mb-2">{label}</p>
      <div className="flex items-end justify-between gap-2 mb-1">
        <div className="font-display text-3xl font-semibold">{value}</div>
        {delta !== null && delta !== undefined && (
          <span className={`text-[10px] font-mono-sm ${delta >= 0 ? "text-green-700" : "text-red-600"}`}>
            {delta >= 0 ? "+" : ""}{delta}%
          </span>
        )}
      </div>
      {sub && <p className="text-xs text-[var(--tmg-muted)]">{sub}</p>}
      {spark && spark.length > 1 && (
        <div className="mt-3 h-10">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={spark}>
              <Line type="monotone" dataKey="v" stroke="#D4AF37" strokeWidth={1.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function QuickAction({ onClick, icon: Icon, label, badge, testId }) {
  return (
    <button onClick={onClick} data-testid={testId} className="w-full flex items-center justify-between bg-white/5 hover:bg-white/10 px-3 py-2.5 text-sm transition-colors">
      <span className="flex items-center gap-3">
        <Icon className="w-4 h-4 text-[var(--tmg-gold)]" />
        {label}
      </span>
      <span className="flex items-center gap-2">
        {badge > 0 && <span className="bg-[var(--tmg-gold)] text-[var(--tmg-obsidian)] text-[10px] font-bold px-1.5 py-0.5">{badge}</span>}
        <ArrowUpRight className="w-3.5 h-3.5 opacity-50" />
      </span>
    </button>
  );
}

function ActivityRow({ item, onJump }) {
  const kindMap = {
    ai_lead: { label: "AI Readiness", color: "#D4AF37", jump: "leads", icon: Sparkles },
    funding_lead: { label: "Funding Readiness", color: "#8B7E5C", jump: "funding", icon: Landmark },
    inquiry: { label: "Discovery Inquiry", color: "#0A0A0A", jump: "inquiries", icon: Inbox },
    contact: { label: "Contact Message", color: "#5C5C5C", jump: "contacts", icon: MessageSquare },
    income: { label: "Income Logged", color: "#16A34A", jump: "income", icon: DollarSign },
  };
  const meta = kindMap[item.kind] || kindMap.contact;
  const Icon = meta.icon;
  const when = item.created_at ? new Date(item.created_at).toLocaleString() : "—";

  return (
    <button
      onClick={() => onJump(meta.jump)}
      className="w-full flex items-center gap-4 px-6 py-4 hover:bg-[var(--tmg-ivory)] text-left"
      data-testid={`activity-${item.kind}-${item.id || ""}`}
    >
      <span className="w-9 h-9 flex items-center justify-center text-white flex-shrink-0" style={{ background: meta.color }}>
        <Icon className="w-4 h-4" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 mb-0.5">
          <p className="font-semibold truncate">{item.name}</p>
          <Badge variant="outline" className="border-[var(--tmg-line)] text-[var(--tmg-muted)] text-[10px] font-mono-sm">
            {meta.label}
          </Badge>
          {item.tier && (
            <Badge variant="outline" className="border-[var(--tmg-gold)] text-[var(--tmg-gold-soft)] text-[10px]">
              {item.tier} {item.score != null ? `· ${item.score}%` : ""}
            </Badge>
          )}
        </div>
        <p className="text-xs text-[var(--tmg-muted)] truncate">
          {item.amount_cents != null
            ? `${fmt(item.amount_cents)} · ${item.title || "—"}`
            : `${item.email || ""}${item.title ? ` · ${item.title}` : ""}`}
        </p>
      </div>
      <span className="text-xs text-[var(--tmg-muted)] font-mono-sm flex-shrink-0">{when}</span>
    </button>
  );
}

/* ===========================================================
   LEADS / SUBMISSIONS LISTS (unchanged behavior)
   =========================================================== */
function LeadsList({ items, endpoint = "leads", onView, onRead, onDelete }) {
  if (!items.length) {
    return <div className="p-16 text-center text-[var(--tmg-muted)]">No leads yet.</div>;
  }
  return (
    <div className="divide-y divide-[var(--tmg-line)]">
      {items.map((it) => (
        <div
          key={it.id}
          data-testid={`${endpoint}-row-${it.id}`}
          className={`flex items-center gap-4 p-5 hover:bg-[var(--tmg-ivory)] transition-colors ${!it.read ? "border-l-2 border-[var(--tmg-gold)]" : ""}`}
        >
          <div className="flex items-center justify-center w-14 h-14 bg-[var(--tmg-obsidian)] text-[var(--tmg-gold)] flex-shrink-0">
            <div className="text-center">
              <div className="font-display text-lg font-semibold leading-none">{it.score_percent}%</div>
              <div className="text-[8px] font-mono-sm uppercase mt-0.5">score</div>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-1">
              <p className="font-semibold truncate">{it.name}</p>
              {!it.read && <Badge className="bg-[var(--tmg-gold)] text-[var(--tmg-obsidian)] hover:bg-[var(--tmg-gold)]">New</Badge>}
              <Badge variant="outline" className="border-[var(--tmg-gold)] text-[var(--tmg-gold-soft)]">{it.score_tier}</Badge>
            </div>
            <p className="text-sm text-[var(--tmg-muted)] truncate">
              {it.email}
              {it.business_name ? ` · ${it.business_name}` : ""}
              {` · → ${it.recommendation}`}
            </p>
            <p className="text-xs text-[var(--tmg-muted)] font-mono-sm mt-1">{new Date(it.created_at).toLocaleString()}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <a href={`${API}/${endpoint}/${it.id}/pdf`} target="_blank" rel="noopener noreferrer" className="p-2 hover:bg-[var(--tmg-ivory-2)]" data-testid={`lead-pdf-${it.id}`} title="Download PDF">
              <Download className="w-4 h-4" />
            </a>
            <button onClick={() => onView(it)} className="p-2 hover:bg-[var(--tmg-ivory-2)]" data-testid={`view-${it.id}`}>
              <Eye className="w-4 h-4" />
            </button>
            {!it.read && (
              <button onClick={() => onRead(it.id)} className="p-2 hover:bg-[var(--tmg-ivory-2)]" title="Mark read" data-testid={`mark-read-${it.id}`}>
                <MailOpen className="w-4 h-4" />
              </button>
            )}
            <button onClick={() => onDelete(it.id)} className="p-2 hover:bg-red-50 hover:text-red-600" data-testid={`delete-${it.id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function SubmissionsList({ items, type, onView, onRead, onDelete }) {
  if (!items.length) return <div className="p-16 text-center text-[var(--tmg-muted)]">No submissions yet.</div>;
  return (
    <div className="divide-y divide-[var(--tmg-line)]">
      {items.map((it) => (
        <div key={it.id} data-testid={`${type}-row-${it.id}`} className={`flex items-center gap-4 p-5 hover:bg-[var(--tmg-ivory)] transition-colors ${!it.read ? "border-l-2 border-[var(--tmg-gold)]" : ""}`}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-1">
              <p className="font-semibold truncate">{it.name}</p>
              {!it.read && <Badge className="bg-[var(--tmg-gold)] text-[var(--tmg-obsidian)] hover:bg-[var(--tmg-gold)]">New</Badge>}
            </div>
            <p className="text-sm text-[var(--tmg-muted)] truncate">
              {it.email}
              {it.business_name ? ` · ${it.business_name}` : ""}
              {it.service_interest ? ` · ${it.service_interest}` : ""}
            </p>
            <p className="text-xs text-[var(--tmg-muted)] font-mono-sm mt-1">{new Date(it.created_at).toLocaleString()}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={() => onView(it)} className="p-2 hover:bg-[var(--tmg-ivory-2)]" data-testid={`view-${it.id}`}><Eye className="w-4 h-4" /></button>
            {!it.read && (
              <button onClick={() => onRead(it.id)} className="p-2 hover:bg-[var(--tmg-ivory-2)]" title="Mark read"><MailOpen className="w-4 h-4" /></button>
            )}
            <button onClick={() => onDelete(it.id)} className="p-2 hover:bg-red-50 hover:text-red-600" data-testid={`delete-${it.id}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

function DetailModal({ selection, onClose, onDelete }) {
  const { item, type } = selection;
  const isInquiry = type === "inquiries";
  const isLead = type === "leads";
  const isFundingLead = type === "funding-leads";
  const isAnyLead = isLead || isFundingLead;
  const pdfEndpoint = isFundingLead ? "funding-leads" : "leads";

  let rows;
  if (isAnyLead) {
    rows = [
      ["Name", item.name], ["Email", item.email], ["Business", item.business_name],
      ["Score", `${item.score_percent}%`], ["Tier", item.score_tier],
      ["Recommended", item.recommendation],
      ["Submitted", new Date(item.created_at).toLocaleString()],
    ];
  } else if (isInquiry) {
    rows = [
      ["Name", item.name], ["Email", item.email], ["Phone", item.phone],
      ["Business", item.business_name], ["Website", item.website],
      ["Service Interest", item.service_interest], ["Looking For", item.looking_for],
      ["Timeline", item.timeline], ["Budget", item.budget], ["Referral", item.referral],
      ["Challenge", item.biggest_challenge],
      ["Submitted", new Date(item.created_at).toLocaleString()],
    ];
  } else {
    rows = [
      ["Name", item.name], ["Email", item.email], ["Phone", item.phone],
      ["Message", item.message], ["Submitted", new Date(item.created_at).toLocaleString()],
    ];
  }

  const headerLabel = isFundingLead ? "Funding Readiness Lead" : isLead ? "AI Readiness Lead" : isInquiry ? "Discovery Inquiry" : "Contact Message";

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="detail-modal">
        <div className="sticky top-0 bg-[var(--tmg-obsidian)] text-white p-6 flex items-center justify-between">
          <div>
            <p className="tmg-overline-gold mb-1">— {headerLabel}</p>
            <h3 className="font-display text-2xl font-semibold">{item.name}</h3>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white text-sm" data-testid="close-modal">Close</button>
        </div>
        <div className="p-6 sm:p-8 space-y-4">
          {rows.map(([k, v]) => (
            <div key={k} className="grid grid-cols-3 gap-4 py-3 border-b border-[var(--tmg-line)]">
              <span className="tmg-overline col-span-1">{k}</span>
              <span className="col-span-2 text-sm whitespace-pre-wrap break-words">{v || "—"}</span>
            </div>
          ))}
          <div className="flex justify-end gap-3 pt-4 flex-wrap">
            {isAnyLead && (
              <a href={`${API}/${pdfEndpoint}/${item.id}/pdf`} target="_blank" rel="noopener noreferrer" className="tmg-btn-secondary">
                <Download className="w-4 h-4" /> Download PDF
              </a>
            )}
            <a href={`mailto:${item.email}?subject=Re: Your inquiry at tmariegreene.com`} className="tmg-btn-secondary">Reply via Email</a>
            <button onClick={() => onDelete(item.id)} className="tmg-btn-dark">
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ===========================================================
   AFFILIATE ANALYTICS (unchanged)
   =========================================================== */
function AffiliateAnalytics({ data, onReload }) {
  const { tools, total_clicks } = data;
  const toolMeta = Object.fromEntries(FUNDING_TOOLS.map((t) => [t.id, t]));
  const maxClicks = Math.max(...tools.map((t) => t.total_clicks), 1);

  const reset = async () => {
    if (!confirm("Reset ALL affiliate click data? This cannot be undone.")) return;
    await api.delete("/admin/affiliate-clicks");
    await onReload();
    toast.success("Click data reset");
  };

  return (
    <div className="p-8 sm:p-10" data-testid="affiliate-analytics">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <p className="tmg-overline mb-2">— Top Performers</p>
          <h2 className="font-display text-2xl font-semibold">
            {total_clicks} total click{total_clicks === 1 ? "" : "s"}
          </h2>
        </div>
        {total_clicks > 0 && (
          <button onClick={reset} data-testid="reset-clicks-btn" className="text-xs font-semibold text-[var(--tmg-muted)] hover:text-red-600 uppercase tracking-[0.14em]">
            Reset Data
          </button>
        )}
      </div>

      {!total_clicks ? (
        <div className="p-16 text-center text-[var(--tmg-muted)]">
          <TrendingUp className="w-10 h-10 mx-auto mb-4 text-[var(--tmg-gold)]" />
          <p className="font-display text-xl mb-2">No affiliate clicks yet.</p>
          <p className="text-sm">Clicks on recommended-tool cards in the Funding Worksheet will appear here.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {tools.map((t, idx) => {
            const meta = toolMeta[t.tool_id] || { name: t.tool_id, category: "Unknown", url: "#" };
            const pct = Math.round((t.total_clicks / maxClicks) * 100);
            const tiers = Object.entries(t.by_tier);
            return (
              <div key={t.tool_id} data-testid={`affiliate-row-${t.tool_id}`} className="border border-[var(--tmg-line)] bg-white p-5">
                <div className="flex items-center justify-between mb-2 gap-4 flex-wrap">
                  <div className="flex items-center gap-4">
                    <span className="font-mono-sm text-xs text-[var(--tmg-gold)] w-8">{String(idx + 1).padStart(2, "0")}</span>
                    <div>
                      <p className="font-display text-lg font-semibold leading-tight">{meta.name}</p>
                      <p className="text-xs text-[var(--tmg-muted)] font-mono-sm uppercase tracking-[0.16em]">
                        {meta.category} · last click {new Date(t.last_click).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="font-display text-3xl font-semibold">{t.total_clicks}</div>
                    <a href={meta.url} target="_blank" rel="noopener nofollow noreferrer" className="p-2 hover:bg-[var(--tmg-ivory-2)]" title="Open affiliate URL">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
                <div className="h-1 bg-[var(--tmg-line)] overflow-hidden mb-3">
                  <div className="h-full bg-[var(--tmg-gold)] transition-all duration-1000" style={{ width: `${pct}%` }} />
                </div>
                {tiers.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {tiers.map(([tier, n]) => (
                      <Badge key={tier} variant="outline" className="border-[var(--tmg-gold)] text-[var(--tmg-gold-soft)]">
                        {tier}: {n}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
